import React, { useState, useEffect, useRef } from 'react';
import { Menu, Dropdown, Alert } from 'antd';
import type { MenuProps } from 'antd';
import { ReactSortable } from 'react-sortablejs';
import { HolderOutlined, EyeInvisibleOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { useMenuStore } from '@shared/store/menuStore';
import { useTranslation } from 'react-i18next';
import { userSettingsApi } from '@/features/settings/api/userSettingsApi';
import { SidebarSectionFlyout } from './SidebarSectionFlyout';
import './DraggableMenu.css';

export type MenuItem = Required<MenuProps>['items'][number];

interface DraggableMenuProps {
  items: MenuItem[];
  selectedKeys?: string[];
  openKeys?: string[];
  onOpenChange?: (keys: string[]) => void;
  onClick?: MenuProps['onClick'];
  theme?: 'light' | 'dark';
  mode?: 'inline' | 'vertical' | 'horizontal';
  inlineCollapsed?: boolean;
}

interface SortableItem {
  id: string;
  item: MenuItem;
}

export function DraggableMenu({
  items,
  selectedKeys,
  openKeys,
  onOpenChange,
  onClick,
  theme = 'light',
  mode = 'inline',
  inlineCollapsed = false,
}: DraggableMenuProps) {
  const { t } = useTranslation(['translation']);
  const { menuOrder, hiddenItems, editMode, hideMenuItem, setMenuOrder } = useMenuStore();
  const menuContainerRef = useRef<HTMLDivElement>(null);

  const effectiveEditMode = editMode && !inlineCollapsed;

  // Фильтруем скрытые элементы и сортируем по сохраненному порядку
  const visibleItems = React.useMemo(() => {
    const filtered = items.filter((item) => {
      const key = item?.key as string;
      return !hiddenItems.includes(key);
    });

    // Сортируем по сохраненному порядку
    return filtered.sort((a, b) => {
      const keyA = a?.key as string;
      const keyB = b?.key as string;
      const orderA = menuOrder.find((m) => m.key === keyA)?.order ?? 999;
      const orderB = menuOrder.find((m) => m.key === keyB)?.order ?? 999;
      return orderA - orderB;
    });
  }, [items, hiddenItems, menuOrder]);

  // Конвертируем в формат для react-sortablejs
  const [sortableItems, setSortableItems] = useState<SortableItem[]>([]);

  useEffect(() => {
    setSortableItems(
      visibleItems.map((item) => ({
        id: item?.key as string,
        item,
      }))
    );
  }, [visibleItems]);

  const handleSortEnd = (newItems: SortableItem[]) => {
    setSortableItems(newItems);
    
    // Обновляем порядок в store
    const newOrder = newItems.map((sortItem, index) => ({
      key: sortItem.id,
      order: index,
    }));
    setMenuOrder(newOrder);
    
    // Сохраняем порядок на backend
    try {
      userSettingsApi.updateMenuOrder(newOrder).catch((error) => {
        console.error('Failed to save menu order:', error);
      });
    } catch (error) {
      console.error('Failed to save menu order:', error);
    }
  };

  // Если режим редактирования отключен, используем стандартное меню Ant Design
  if (!effectiveEditMode) {
    // В свёрнутом (rail) режиме openKeys НЕ передаём — Menu должен сам управлять
    // открытием popup-подменю по hover (unctrolled). Передача [] делает Menu
    // контролируемым и намертво блокирует hover-popup: rc-menu просит открыть
    // подменю через onOpenChange, а openKeys снаружи всегда остаётся [].
    const currentOpenKeys = inlineCollapsed ? undefined : (openKeys ?? []);

    return (
      <div ref={menuContainerRef} style={{ position: 'relative' }}>
        <Menu
          key={`menu-${inlineCollapsed ? 'collapsed' : 'expanded'}-${(currentOpenKeys ?? []).join(',')}`}
          theme={theme}
          mode={mode}
          selectedKeys={selectedKeys}
          openKeys={currentOpenKeys}
          onOpenChange={(keys) => {
            if (!inlineCollapsed && onOpenChange) {
              // Просто передаем keys как есть - БЕЗ модификации!
              onOpenChange(keys);
            }
          }}
          onClick={onClick}
          items={visibleItems}
          inlineCollapsed={inlineCollapsed}
          style={{ borderRight: 0 }}
          // Показывать popup при hover в свернутом состоянии
          triggerSubMenuAction={inlineCollapsed ? 'hover' : 'click'}
          // Плавная анимация ТОЛЬКО для inline-раскрытия (max-height коллапс).
          // Для свёрнутого rail-режима подменю рендерится floating popup'ом
          // (position: absolute) — motionName="ant-motion-collapse" ждёт
          // transitionend от max-height-анимации, который на floating-элементе
          // не срабатывает, и rc-motion никогда не убирает узел из DOM: старые
          // popup'ы копятся друг под другом при последовательном hover.
          // В collapsed-режиме отдаём antd его дефолтную popup-анимацию.
          motion={
            inlineCollapsed
              ? undefined
              : {
                  motionName: 'ant-motion-collapse',
                  motionAppear: false,
                  motionEnter: true,
                  motionLeave: true,
                }
          }
        />
        {/* GitLab-паттерн: в раскрытом сайдбаре hover на ЕЩЁ НЕ раскрытом
            кликом пункте показывает всплывающее подменю сбоку. Раскрытому
            (inline, push-down) пункту флаут не нужен — дети уже видны. */}
        <SidebarSectionFlyout
          containerRef={menuContainerRef}
          items={visibleItems}
          openKeys={currentOpenKeys ?? []}
          onClick={onClick}
          isDark={theme === 'dark'}
          enabled={!inlineCollapsed}
        />
      </div>
    );
  }

  // В режиме редактирования показываем упрощенный список
  return (
    <div>
      {/* Подсказка в режиме редактирования */}
      <div className="px-4 py-2">
        <Alert
          message={t('menu_settings.edit_mode_hint_short') || 'Перетащите пункты для изменения порядка'}
          type="info"
          icon={<InfoCircleOutlined />}
          closable={false}
          banner
          className="text-xs"
        />
      </div>

      <ReactSortable
        list={sortableItems}
        setList={handleSortEnd}
        animation={200}
        handle=".drag-handle"
        ghostClass="sortable-ghost"
        className={`ant-menu ant-menu-${theme} ant-menu-root ant-menu-${mode}`}
        style={{ borderRight: 0 }}
      >
        {sortableItems.map((sortItem) => {
          const { item } = sortItem;
          const key = item?.key as string;
          const label = item && 'label' in item ? item.label : '';
          const icon = item && 'icon' in item ? item.icon : undefined;
          const hasChildren = item && 'children' in item && item.children;
          const childrenCount = hasChildren ? (item.children as MenuItem[]).length : 0;

          // Контекстное меню для скрытия
          const contextMenu = {
            items: [
              {
                key: 'hide',
                label: t('menu_settings.hide_item') || 'Скрыть из меню',
                icon: <EyeInvisibleOutlined />,
                onClick: () => hideMenuItem(key),
              },
            ],
          };

          return (
            <div
              key={key}
              data-id={key}
              className="ant-menu-item"
              style={{
                padding: '0 16px',
                height: '36px',
                lineHeight: '36px',
                display: 'flex',
                alignItems: 'center',
                cursor: 'default',
                transition: 'all 0.2s',
              }}
            >
              <Dropdown menu={contextMenu} trigger={['contextMenu']}>
                <div className="flex items-center gap-2 w-full">
                  <span
                    className="drag-handle opacity-50 hover:opacity-100 cursor-move"
                    title={t('menu_settings.drag_hint') || 'Перетащите для изменения порядка'}
                  >
                    <HolderOutlined />
                  </span>
                  {icon && <span className="anticon">{icon}</span>}
                  <span className="flex-1">{label}</span>
                  {hasChildren && (
                    <span className="text-xs opacity-50">({childrenCount})</span>
                  )}
                </div>
              </Dropdown>
            </div>
          );
        })}
      </ReactSortable>

      {/* Информация о подменю */}
      <div className="px-4 py-2">
        <Alert
          message={t('menu_settings.submenu_hidden_hint') || 'Подменю скрыты в режиме редактирования. Выключите режим редактирования для доступа к подразделам.'}
          type="warning"
          showIcon
          closable={false}
          className="text-xs"
        />
      </div>
    </div>
  );
}
